import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useShopVertical } from "../../lib/useShopVertical";
import { clsx } from "clsx";
import { Loader2, Sparkles, Eye, AlertTriangle, CheckCircle2, Upload, Download } from "lucide-react";
import { productionOf, type ApparelStudy, type StudyLayer, type StudyTone } from "@erc/shared";
import { useStudioModel, customApi, type CustomListingDetail, type GlyphSetView } from "../../lib/customListing";
import { InspirationPanel } from "./InspirationPanel";
import { InkSelect, PhotoInput, inkLayersOf, photoLayersOf } from "./ExtraValueInputs";
import { fidelityOptionsFor, isNearFidelity, type Fidelity } from "../../lib/designPrompt";
import { apiClient } from "../../lib/api";
import { authHeaders, getBackendUrl } from "../../lib/http";

// Custom Liste Studyosu 3. adim: tasarim ve sablon. Sira:
//  1) taban tasarim (ilhamdaki tek urunun baskisi, ornek degerlerle)
//  2) harf harf cizilmis yazilar icin harf seti
//  3) duzenlenebilir sablon + deger ve tisort tonuyla onizleme

const TONE_LABEL: Record<StudyTone, string> = { light: "Açık tişört", mid: "Orta ton", dark: "Koyu tişört" };
// Onizleme zemini: baskinin tisort uzerinde nasil gorunecegine yakin (icerik rengi)
const TONE_BG: Record<StudyTone, string> = { light: "#f4f1ec", mid: "#9a9a98", dark: "#2f2f2f" };

function Status({ s }: { s?: string }) {
  if (s === "ready") return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Hazır</span>;
  if (s === "running") return <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700"><Loader2 className="h-3 w-3 animate-spin" /> Üretiliyor</span>;
  if (s === "failed") return <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600">Başarısız</span>;
  return null;
}

function ModelSelect({ id, rec, value, onChange }: { id: string; rec: CustomListingDetail; value: string; onChange: (v: string) => void }) {
  return (
    <>
      <label htmlFor={id} className="sr-only">Görsel modeli</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
        {rec.imageModels.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
      </select>
    </>
  );
}

export function TemplateStep({ rec, onChanged }: { rec: CustomListingDetail; onChanged: () => void }) {
  const study = rec.study as ApparelStudy | null;
  const images = study?.imageUrls?.length ? study.imageUrls : (rec.source.imageUrls ?? []);
  if (!study?.layers || study.status !== "approved") {
    return (
      <>
        <InspirationPanel images={images} url={rec.analysis?.url} title={rec.name} />
        <p className="text-sm text-gray-600">Bu adım onaylı liste çalışmasına dayanır: hangi yazıyı alıcının değiştirdiği, hangisinin harf harf çizim olduğu oradan gelir. Önce 2. adımda çalışmayı onayla.</p>
      </>
    );
  }
  const textLayers = study.layers.filter((l) => l.source === "buyer_text" && (l.kind === "font_text" || l.kind === "glyph_text"));
  const glyphLayers = textLayers.filter((l) => l.kind === "glyph_text");
  const offFront = study.layers.filter((l) => l.source !== "fixed" && l.side !== "front");
  return (
    <div className="space-y-5">
      <InspirationPanel images={images} url={rec.analysis?.url} title={rec.name} />
      {offFront.length > 0 && (
        <p className="flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          {offFront.map((l) => l.labelTr).join(", ")} ön yüzde değil. Şimdilik yalnız ön baskının şablonu kurulur; arka, kol ve cep baskıları sonraki aşamada eklenecek.
        </p>
      )}
      {productionOf(study).mode === "ai" && rec.template.pkg?.status !== "ready" && (
        <p className="rounded-lg border border-violet-200 bg-violet-50/60 p-3 text-sm text-gray-700">
          <span className="font-medium text-gray-900">Bu liste AI yolunda; bu adım atlandı.</span> {productionOf(study).reasonTr} Örnekler 4. adımda "AI ile örnekleri çiz" ile hazırlanır. Yine de şablon kurmak istersen aşağıdan devam edebilir ya da 2. adımda yolu değiştirebilirsin.
        </p>
      )}
      {productionOf(study).mode !== "ai" && rec.template.pkg?.status !== "ready" && (
        <p className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 text-xs text-gray-700">
          <span className="font-medium text-gray-900">Önce hızlıca liste mi hazırlamak istiyorsun?</span> Bu adımı şimdilik atlayıp 4. adımda "Hızlı liste"yi kullanabilirsin: örnek tasarımlar şablonsuz AI ile çizilir, liste hazırlanır. Taban tasarım, harf seti ve şablon sipariş gelince burada tamamlanır.
        </p>
      )}
      <BaseCard rec={rec} study={study} images={images} onChanged={onChanged} />
      {glyphLayers.map((l, i) => <GlyphCard key={l.key} n={2 + i} rec={rec} layer={l} onChanged={onChanged} />)}
      <TemplateCard n={2 + glyphLayers.length} rec={rec} study={study} textLayers={textLayers} onChanged={onChanged} />
    </div>
  );
}

function BaseCard({ rec, study, images, onChanged }: { rec: CustomListingDetail; study: ApparelStudy; images: string[]; onChanged: () => void }) {
  const base = rec.template.base;
  const [ref, setRef] = useState(base?.referenceImageUrl ?? images[0] ?? "");
  const [model, setModel] = useStudioModel(rec);
  // IP sIkI modlu magazada Yakin/Benzer yok, varsayilan Dengeli
  const ipStrict = !!useShopVertical().shop?.ipStrict;
  const FIDELITY_OPTIONS = fidelityOptionsFor(ipStrict);
  const [fidelity, setFidelity] = useState<Fidelity>(() => { const f = (base?.fidelity as Fidelity) ?? "close"; return ipStrict && isNearFidelity(f) ? "balanced" : f; });
  const [mode, setMode] = useState<"generate" | "existing">(base?.source === "existing" ? "existing" : "generate");
  const [err, setErr] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: () => customApi("POST", `/${rec.id}/base`, { referenceImageUrl: ref, model, fidelity }),
    onSuccess: () => { setErr(null); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  const running = base?.status === "running";
  const fidHint = FIDELITY_OPTIONS.find((f) => f.key === fidelity)?.hint;
  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="mb-1 flex items-center gap-2"><h3 className="text-sm font-semibold text-gray-900">1. Taban tasarım</h3><Status s={base?.status} /></div>
      <p className="mb-3 text-xs text-gray-500">
        İlham fotoğrafındaki tek ürünün baskısı yeniden çizilir; alıcının değiştirdiği yazılar örnek değerlerle yazılır{(() => { const inRef = study.items.filter((i) => study.imageUrls[i.imageIndex] === ref); return inRef.length > 1 ? ` (seçili fotoğrafta ${inRef.length} ürün var, ${inRef[0]?.productColor ?? "ilk"} ürün esas alınır; tek ürünlü yakın çekim seçmek daha net sonuç verir)` : ""; })()}. {rec.isGarment !== false ? "Tona göre değişen renkler açık tişörte göre çizilir; koyu tişört rengi şablonda ayarlanır. " : "Tasarım ürünün baskı alanına (yuvarlak/kalp) göre zeminsiz çizilir. "}Elinde hazır tasarım varsa üretmeden onu seçip doğrudan şablona geçebilirsin.
      </p>
      <div className="mb-3 inline-flex rounded-lg border border-gray-200 p-0.5" role="group" aria-label="Taban tasarım kaynağı">
        {([["generate", "AI ile üret"], ["existing", "Hazır tasarımı kullan"]] as const).map(([k, label]) => (
          <button key={k} onClick={() => setMode(k)} aria-pressed={mode === k}
            className={clsx("rounded-md px-3 py-1.5 text-xs font-medium", mode === k ? "bg-indigo-50 text-indigo-700" : "text-gray-600 hover:bg-gray-50")}>{label}</button>
        ))}
      </div>
      {mode === "existing" ? <ExistingBase rec={rec} study={study} onChanged={onChanged} /> : (<>
      <div className="mb-3">
        <p className="mb-1 text-xs font-medium text-gray-700">İlhama yakınlık</p>
        <div className="inline-flex flex-wrap rounded-lg border border-gray-200 p-0.5" role="group" aria-label="İlhama yakınlık">
          {FIDELITY_OPTIONS.filter((f) => f.key !== "original").map((f) => (
            <button key={f.key} onClick={() => setFidelity(f.key)} aria-pressed={fidelity === f.key}
              className={clsx("rounded-md px-3 py-1.5 text-xs font-medium", fidelity === f.key ? "bg-indigo-50 text-indigo-700" : "text-gray-600 hover:bg-gray-50")}>{f.label}</button>
          ))}
        </div>
        <p className="mt-1 text-xs text-gray-500">{fidHint}{fidelity !== "close" ? ". Alıcı yazıları aynı kalır; çizim değişir." : ""}</p>
      </div>
      {images.length > 1 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {images.map((u) => (
            <button key={u} onClick={() => setRef(u)} aria-pressed={ref === u} title="Referans fotoğraf"
              className={clsx("h-16 w-16 overflow-hidden rounded-lg border-2", ref === u ? "border-indigo-500" : "border-transparent hover:border-gray-300")}>
              <img src={u} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <ModelSelect id="base-model" rec={rec} value={model} onChange={setModel} />
        <button onClick={() => run.mutate()} disabled={!ref || run.isPending || running}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
          {run.isPending || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {running ? "Çiziliyor…" : base?.status === "ready" ? "Yeniden üret" : "Taban tasarımı üret"}
        </button>
        <span className="text-xs text-gray-500">yaklaşık 1 dakika</span>
      </div>
      </>)}
      {base?.status === "failed" && <p className="mt-2 text-sm text-red-600">{base.error ?? "Üretilemedi."}</p>}
      {base?.status === "ready" && base.url && (
        <div className="mt-3 flex flex-wrap items-start gap-4">
          <img src={base.url} alt="Taban tasarım" className="max-h-72 rounded-lg border border-gray-200 object-contain" style={{ background: TONE_BG.light }} />
          {base.values && (
            <div className="text-xs text-gray-600">
              <p className="mb-1 font-medium text-gray-700">Yazılan örnek değerler</p>
              {Object.entries(base.values).map(([k, v]) => <p key={k}>{study.layers.find((l) => l.key === k)?.labelTr ?? k}: <span className="text-gray-900">{v}</span></p>)}
              <p className="mt-2 max-w-xs text-gray-500">Yazılar doğru ve stil ilhamla tutarlıysa sonraki karta geç; değilse yeniden üret.</p>
            </div>
          )}
        </div>
      )}
      {err && <p className="mt-2 text-sm text-red-600">{err}</p>}
    </div>
  );
}

/** Hazir tasarimla sablona gecis: bu analizin tasarimlarindan secilir ya da dosya yuklenir.
 *  Sablon kurucu tasarimdaki yazilari bu degerlerle arayip katmana cevirir. */
function ExistingBase({ rec, study, onChanged }: { rec: CustomListingDetail; study: ApparelStudy; onChanged: () => void }) {
  const base = rec.template.base;
  const textLayers = study.layers.filter((l) => l.source === "buyer_text" && (l.kind === "font_text" || l.kind === "glyph_text"));
  const [pick, setPick] = useState(base?.source === "existing" ? base.url ?? "" : "");
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(textLayers.map((l) => [l.key, base?.values?.[l.key] ?? ""])));
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: async (f: File) => {
      const dataUrl = await new Promise<string>((ok, bad) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => bad(r.error); r.readAsDataURL(f); });
      return apiClient.uploadAnalysisDesign(rec.analysis!.id, dataUrl);
    },
    onSuccess: (d) => { setErr(null); if (d.imageUrl) setPick(/^https?:/.test(d.imageUrl) ? d.imageUrl : `${getBackendUrl()}${d.imageUrl}`); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  const save = useMutation({
    mutationFn: () => customApi("POST", `/${rec.id}/base`, { designUrl: pick, values }),
    onSuccess: () => { setErr(null); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  const choices = [...new Set([...(pick ? [pick] : []), ...rec.designImages])];
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">Bu analizde üretilmiş ya da yüklenmiş bir tasarımı seç. Tasarımda alıcının değiştireceği yazılar örnek bir değerle yazılı olmalı; o değerleri aşağıya aynen gir, şablon kurulurken bulunup katmana çevrilir.</p>
      <div className="flex flex-wrap gap-2">
        {choices.map((u) => (
          <button key={u} onClick={() => setPick(u)} aria-pressed={pick === u} title="Taban tasarım olarak seç"
            className={clsx("h-20 w-20 overflow-hidden rounded-lg border-2 bg-gray-50", pick === u ? "border-indigo-500" : "border-transparent hover:border-gray-300")}>
            <img src={u} alt="" loading="lazy" className="h-full w-full object-contain" />
          </button>
        ))}
        {rec.analysis && (
          <button onClick={() => fileRef.current?.click()} disabled={upload.isPending}
            className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-300 text-xs text-gray-600 hover:bg-gray-50">
            {upload.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Yükle
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" aria-label="Tasarım dosyası yükle"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ""; }} />
      </div>
      {choices.length === 0 && <p className="text-xs text-gray-500">Bu analizde tasarım yok; dosya yükle.</p>}
      <div className="grid gap-2 sm:grid-cols-2">
        {textLayers.map((l) => (
          <div key={l.key}>
            <label htmlFor={`eb-${l.key}`} className="mb-1 block text-xs font-medium text-gray-700">Tasarımda yazan: {l.labelTr}</label>
            <input id={`eb-${l.key}`} value={values[l.key] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [l.key]: e.target.value }))} maxLength={80}
              placeholder={l.sampleValue ?? ""}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          </div>
        ))}
      </div>
      <button onClick={() => save.mutate()} disabled={!pick || save.isPending || !Object.values(values).some((v) => v.trim())}
        className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
        {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Taban tasarım olarak kullan
      </button>
      {err && <p className="text-sm text-red-600">{err}</p>}
    </div>
  );
}

function GlyphCard({ n, rec, layer, onChanged }: { n: number; rec: CustomListingDetail; layer: StudyLayer; onChanged: () => void }) {
  const set: GlyphSetView | undefined = rec.template.glyphSets?.[layer.key];
  const [ref, setRef] = useState<string>(set?.styleRefUrl ?? rec.refImages[0] ?? "");
  const [model, setModel] = useStudioModel(rec);
  const [text, setText] = useState(layer.suggestedValues?.[0] ?? layer.sampleValue ?? "");
  const [preview, setPreview] = useState<{ url: string; missing: string[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const build = useMutation({
    mutationFn: () => customApi("POST", `/${rec.id}/glyphs`, { layerKey: layer.key, styleRefUrl: ref, model }),
    onSuccess: () => { setErr(null); setPreview(null); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  const show = useMutation({
    mutationFn: () => customApi<{ url: string; missing: string[] }>("POST", `/${rec.id}/glyphs/${layer.key}/preview`, { text }),
    onSuccess: (r) => { setErr(null); setPreview(r); },
    onError: (e) => setErr((e as Error).message),
  });
  const running = set?.status === "running";

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-900">{n}. Harf seti: {layer.labelTr}</h3>
        <Status s={set?.status} />
        {set?.status === "ready" && <span className="text-xs text-gray-500">{Object.keys(set.glyphs).length} karakter</span>}
      </div>
      <p className="mb-3 text-xs text-gray-500">Bu yazının her harfi ayrı çizilmiş; fontla basılamaz. Tasarımın stilinde bir alfabe bir kez üretilir (yaklaşık 1 dakika), sonra her değer bu harflerden kodla dizilir. Referans olarak harfleri en net gösteren görseli seç; taban tasarım hazırsa ilk sıradadır.</p>
      <div className="mb-3 flex flex-wrap gap-2">
        {rec.refImages.map((u) => (
          <button key={u} onClick={() => setRef(u)} aria-pressed={ref === u}
            className={clsx("h-16 w-16 overflow-hidden rounded-lg border-2 bg-gray-50", ref === u ? "border-indigo-500" : "border-transparent hover:border-gray-300")}>
            <img src={u} alt="" loading="lazy" className="h-full w-full object-contain" />
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <ModelSelect id={`gm-${layer.key}`} rec={rec} value={model} onChange={setModel} />
        <button onClick={() => build.mutate()} disabled={!ref || build.isPending || running}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
          {build.isPending || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {running ? "Harfler üretiliyor…" : set?.status === "ready" ? "Yeniden üret" : "Harf setini üret"}
        </button>
      </div>
      {set?.status === "failed" && <p className="mt-2 text-sm text-red-600">{set.error ?? "Harf seti üretilemedi."}</p>}
      {set?.status === "ready" && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-1 rounded-lg bg-gray-50 p-2">
            {Object.entries(set.glyphs).map(([ch, url]) => <img key={ch} src={url} alt={ch} title={ch} className="h-8 w-auto" loading="lazy" />)}
          </div>
          {set.cycleColors.length > 0 && (
            <p className="flex flex-wrap items-center gap-1.5 text-xs text-gray-600">
              Karo renkleri bu sırayla döner:
              {set.cycleColors.map((c) => <span key={c} className="h-3.5 w-3.5 rounded-full border border-gray-300" style={{ background: c }} title={c} />)}
            </p>
          )}
          {set.missing.length > 0 && <p className="flex items-center gap-1 text-xs text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> Okunamayan karakterler: {set.missing.join(" ")}. Yeniden üretmeyi dene.</p>}
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor={`gp-${layer.key}`} className="sr-only">Önizlenecek değer</label>
            <input id={`gp-${layer.key}`} value={text} onChange={(e) => setText(e.target.value)} maxLength={40} placeholder="Bir değer yaz"
              className="w-56 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
            <button onClick={() => show.mutate()} disabled={!text.trim() || show.isPending}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {show.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Önizle
            </button>
          </div>
          {preview && <img src={preview.url} alt={text} className="max-h-24 max-w-full rounded-lg border border-gray-200 bg-white p-2" />}
        </div>
      )}
      {err && <p className="mt-2 text-sm text-red-600">{err}</p>}
    </div>
  );
}

function TemplateCard({ n, rec, study, textLayers, onChanged }: { n: number; rec: CustomListingDetail; study: ApparelStudy; textLayers: StudyLayer[]; onChanged: () => void }) {
  const base = rec.template.base;
  const pkg = rec.template.pkg;
  const [artMode, setArtMode] = useState<"inpaint" | "ai">("inpaint");
  const [model, setModel] = useStudioModel(rec);
  const inkLayers = inkLayersOf(study.layers), photoLayers = photoLayersOf(study.layers);
  const hasPhoto = photoLayers.length > 0;
  const [values, setValues] = useState<Record<string, string>>(() => ({
    ...Object.fromEntries(textLayers.map((l) => [l.key, l.suggestedValues?.[0] ?? base?.values?.[l.key] ?? l.sampleValue ?? ""])),
    // Murekkep: ilk ornek urunun secimi (ya da ilk secenek)
    ...Object.fromEntries(inkLayers.map((l) => [l.key, study.items[0]?.values[l.key] ?? l.options?.[0] ?? ""])),
  }));
  const tones = (study.palettes.length ? study.palettes.map((p) => p.tone) : ["light", "dark"]) as StudyTone[];
  const [tone, setTone] = useState<StudyTone>(tones[0]);
  const [preview, setPreview] = useState<{ url: string; tone: StudyTone } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const build = useMutation({
    mutationFn: () => customApi("POST", `/${rec.id}/template`, { artMode, model }),
    onSuccess: () => { setErr(null); setPreview(null); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  // Fotograf/baski efektleri: ornek, mockup ve siparis ayni ayarla basilir
  const tplFx = (pkg?.template as { effects?: { photoMono?: boolean; knockout?: { threshold: number } | null } } | undefined)?.effects;
  const photoNote = study.layers.find((l) => l.kind === "photo_slot");
  const [mono, setMono] = useState<boolean>(tplFx?.photoMono ?? (!!photoNote && /siyah[- ]beyaz|black[- ]?(and|&)[- ]?white|grayscale|monochrome/i.test(`${photoNote.noteTr ?? ""} ${photoNote.fixedColor?.name ?? ""}`)));
  const [ko, setKo] = useState<number | null>(tplFx?.knockout?.threshold ?? null);
  const saveFx = useMutation({
    mutationFn: (fx: { photoMono?: boolean; knockout?: { threshold: number } | null }) => customApi("POST", `/${rec.id}/template-effects`, fx),
    onSuccess: () => { onChanged(); render.mutate(); },
    onError: (e) => setErr((e as Error).message),
  });
  const render = useMutation({
    mutationFn: () => customApi<{ url: string }>("POST", `/${rec.id}/render`, { values, tone }),
    onSuccess: (r) => { setErr(null); setPreview({ url: r.url, tone }); },
    onError: (e) => setErr((e as Error).message),
  });
  // Secilen tonun baski dosyasi (acik/koyu tisort tasarimi), siparis dosyasiyla ayni yol
  const download = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${getBackendUrl()}/api/custom-listings/${rec.id}/order-print`, {
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ values, tone }),
      });
      if (!res.ok) throw new Error(((await res.json().catch(() => null)) as { error?: string } | null)?.error ?? `Baskı dosyası hazırlanamadı (${res.status})`);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(await res.blob());
      a.download = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? `tasarim-${tone}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    },
    onSuccess: () => setErr(null),
    onError: (e) => setErr((e as Error).message),
  });
  const running = pkg?.status === "running";
  const glyphPending = textLayers.some((l) => l.kind === "glyph_text" && rec.template.glyphSets?.[l.key]?.status !== "ready");

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="mb-1 flex items-center gap-2"><h3 className="text-sm font-semibold text-gray-900">{n}. Düzenlenebilir şablon</h3><Status s={pkg?.status} /></div>
      <p className="mb-3 text-xs text-gray-500">Taban tasarımdaki alıcı yazıları bulunur, çizimden silinir ve katmana çevrilir. Sonra her değer{rec.isGarment !== false ? " ve her tişört tonu" : ""} bu şablondan basılır; örnekler, kolaj ve sipariş dosyası hep aynı şablondan çıkar.</p>
      {base?.status !== "ready" ? (
        <p className="text-sm text-gray-500">Önce taban tasarımı üret.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {hasPhoto ? (
            <>
              <span className="text-xs text-gray-600">Fotoğraflı tasarım: fotoğraf ve yazılar AI ile silinir, fotoğraf ayrı katman olur (~$0.03).</span>
              <ModelSelect id="tpl-model" rec={rec} value={model} onChange={setModel} />
            </>
          ) : (
            <>
              <label htmlFor="art-mode" className="sr-only">Yazı silme yöntemi</label>
              <select id="art-mode" value={artMode} onChange={(e) => setArtMode(e.target.value as "inpaint" | "ai")}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
                <option value="inpaint">Yazıyı zeminle doldurarak sil (ücretsiz; yazı düz zemindeyse)</option>
                <option value="ai">Yazıyı AI ile sil (~$0.03; yazı fotoğrafa/çizime biniyorsa, çizim korunur)</option>
              </select>
              {artMode === "ai" && <ModelSelect id="tpl-model" rec={rec} value={model} onChange={setModel} />}
            </>
          )}
          <button onClick={() => build.mutate()} disabled={build.isPending || running}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
            {build.isPending || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {running ? "Şablon kuruluyor…" : pkg?.status === "ready" ? "Şablonu yeniden kur" : "Şablona çevir"}
          </button>
        </div>
      )}
      {pkg?.status === "failed" && <p className="mt-2 text-sm text-red-600">{pkg.error ?? "Şablon kurulamadı."}</p>}
      {pkg?.status === "ready" && (
        <div className="mt-4 space-y-3">
          {pkg.note && <p className="flex items-start gap-1 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> {pkg.note}</p>}
          {glyphPending && <p className="flex items-center gap-1 text-xs text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> Harf seti hazır olmayan yazılar şimdilik fontla basılır.</p>}
          <div className="grid gap-2 sm:grid-cols-2">
            {textLayers.map((l) => (
              <div key={l.key}>
                <label htmlFor={`tv-${l.key}`} className="mb-1 block text-xs font-medium text-gray-700">{l.labelTr}</label>
                <input id={`tv-${l.key}`} value={values[l.key] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [l.key]: e.target.value }))} maxLength={80}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
              </div>
            ))}
            {inkLayers.map((l) => (
              <div key={l.key}>
                <span className="mb-1 block text-xs font-medium text-gray-700">{l.labelTr} (alıcı seçer)</span>
                <InkSelect layer={l} value={values[l.key] ?? ""} onChange={(v) => setValues((x) => ({ ...x, [l.key]: v }))} />
              </div>
            ))}
            {photoLayers.map((l) => (
              <div key={l.key}>
                <span className="mb-1 block text-xs font-medium text-gray-700">{l.labelTr}</span>
                <PhotoInput recId={rec.id} layer={l} value={values[l.key] ?? ""} onChange={(v) => setValues((x) => ({ ...x, [l.key]: v }))} />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {rec.isGarment !== false && (
              <div className="inline-flex rounded-lg border border-gray-200 p-0.5" role="group" aria-label="Tişört tonu">
                {tones.map((t) => (
                  <button key={t} onClick={() => setTone(t)} aria-pressed={tone === t}
                    className={clsx("rounded-md px-3 py-1.5 text-xs font-medium", tone === t ? "bg-indigo-50 text-indigo-700" : "text-gray-600 hover:bg-gray-50")}>{TONE_LABEL[t]}</button>
                ))}
              </div>
            )}
            <button onClick={() => render.mutate()} disabled={render.isPending}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {render.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Önizle
            </button>
            {photoNote && (
              <label className="inline-flex items-center gap-1.5 text-xs text-gray-700" title="Alıcının fotoğrafı siyah-beyaz basılır (örnek, mockup ve sipariş)">
                <input type="checkbox" checked={mono} onChange={(e) => { setMono(e.target.checked); saveFx.mutate({ photoMono: e.target.checked }); }} className="rounded border-gray-300 text-indigo-600" />
                Fotoğraf siyah-beyaz
              </label>
            )}
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-700" title="Siyaha yakın pikseller şeffaf olur, tişörtün rengi görünür (vintage, kumaşa işlemiş görünüm). Açık tişörtte uygulanmaz.">
              <input type="checkbox" checked={ko !== null} onChange={(e) => { const v = e.target.checked ? 60 : null; setKo(v); saveFx.mutate({ knockout: v === null ? null : { threshold: v } }); }} className="rounded border-gray-300 text-indigo-600" />
              Siyahı kumaşa aç
            </label>
            {ko !== null && (
              <input type="range" min={10} max={140} step={5} value={ko} aria-label="Siyah eşiği"
                onChange={(e) => setKo(Number(e.target.value))} onMouseUp={() => saveFx.mutate({ knockout: { threshold: ko } })} onTouchEnd={() => saveFx.mutate({ knockout: { threshold: ko } })} onKeyUp={() => saveFx.mutate({ knockout: { threshold: ko } })}
                className="w-28 accent-indigo-600" title={`Eşik ${ko}`} />
            )}
            <button onClick={() => download.mutate()} disabled={download.isPending}
              title={rec.isGarment !== false ? `${TONE_LABEL[tone]} için baskı dosyası (300 DPI PNG)` : "Baskı dosyası (PNG)"}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {download.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Tasarımı indir
            </button>
          </div>
          {preview && <img src={preview.url} alt="Şablon önizlemesi" className="max-h-96 rounded-lg border border-gray-200 object-contain" style={{ background: TONE_BG[preview.tone] }} />}
        </div>
      )}
      {err && <p className="mt-2 text-sm text-red-600">{err}</p>}
    </div>
  );
}
